import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchPlayerPercentiles } from "../services/player-service";
import { isBattingTab } from "../utils/playerDataUtils";

export const usePercentiles = ({
  playerId,
  playerYears,
  activeTab,
  division,
  conference,
  selectedYear,
  fallbackYearsByTab,
}) => {
  const cacheRef = useRef(new Map());
  const inFlightRef = useRef(new Map());
  const [percentiles, setPercentiles] = useState({ batting: null, pitching: null });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const tabType = useMemo(() => (isBattingTab(activeTab) ? "batting" : "pitching"), [activeTab]);

  const availableYears = useMemo(() => {
    const key = tabType === "batting" ? "batting_years" : "pitching_years";
    const fromPlayerYears = playerYears?.[key] || [];
    if (fromPlayerYears.length) return fromPlayerYears;
    return fallbackYearsByTab?.[key] || [];
  }, [playerYears, tabType, fallbackYearsByTab]);

  const mostRecentYear = useMemo(() => {
    const fromPlayerYears =
      tabType === "batting"
        ? playerYears?.most_recent_batting
        : playerYears?.most_recent_pitching;
    if (fromPlayerYears) return fromPlayerYears;
    return tabType === "batting"
      ? fallbackYearsByTab?.most_recent_batting
      : fallbackYearsByTab?.most_recent_pitching || null;
  }, [playerYears, tabType, fallbackYearsByTab]);

  const targetYear = useMemo(() => {
    if (!availableYears?.length) return null;
    if (selectedYear && availableYears.includes(selectedYear)) return selectedYear;
    return mostRecentYear || null;
  }, [availableYears, selectedYear, mostRecentYear]);

  const normalizedConference = conference === "conference" ? "auto" : conference || "all";

  const keyFor = useCallback(
    (year) => `${playerId}-${year}-${division}-${normalizedConference}`,
    [playerId, division, normalizedConference]
  );

  const isDataCached = useCallback(
    (year) => {
      if (!year) return false;
      return cacheRef.current.has(keyFor(year));
    },
    [keyFor]
  );

  const doFetch = useCallback(
    async (year) => {
      const cacheKey = keyFor(year);
      if (cacheRef.current.has(cacheKey)) return cacheRef.current.get(cacheKey);
      if (inFlightRef.current.has(cacheKey)) return inFlightRef.current.get(cacheKey);

      const promise = fetchPlayerPercentiles(playerId, year, division, normalizedConference)
        .then((resp) => {
          cacheRef.current.set(cacheKey, resp);
          return resp;
        })
        .finally(() => {
          inFlightRef.current.delete(cacheKey);
        });

      inFlightRef.current.set(cacheKey, promise);
      return promise;
    },
    [division, keyFor, normalizedConference, playerId]
  );

  useEffect(() => {
    const controller = new AbortController();

    (async () => {
      setError(null);
      if (!playerId || !division || !targetYear) {
        setPercentiles({ batting: null, pitching: null });
        return;
      }

      if (isDataCached(targetYear)) {
        const cachedData = cacheRef.current.get(keyFor(targetYear));
        if (!controller.signal.aborted) setPercentiles(cachedData);
        return;
      }

      setIsLoading(true);
      try {
        const resp = await doFetch(targetYear);
        if (!controller.signal.aborted) setPercentiles(resp);
      } catch (e) {
        if (e.name !== "AbortError") setError(e);
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    })();

    return () => controller.abort();
  }, [playerId, division, targetYear, normalizedConference, doFetch, isDataCached, keyFor]);

  const prefetchYears = useCallback(
    (years) => {
      if (!years?.length) return;
      years.forEach((y) => {
        if (!y) return;
        const cacheKey = keyFor(y);
        if (!cacheRef.current.has(cacheKey) && !inFlightRef.current.has(cacheKey)) {
          doFetch(y).catch(() => {});
        }
      });
    },
    [doFetch, keyFor]
  );

  return {
    percentiles,
    isLoading,
    error,
    tabType,
    availableYears,
    mostRecentYear,
    targetYear,
    prefetchYears,
  };
};
